import type { JSONB } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateIngestionConfigMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface UpdateIngestionConfigInput {
  Id: string;
  OriginalTimestamp: string;
  // The config blob consumers hold is a parsed JSON object (`JSON.parse` /
  // spread literal); the tRPC procedure input is the shared `JSONB` type. The
  // runtime values are identical, so the public input takes the loose shape and
  // we narrow to `JSONB` at the single mutateAsync boundary below.
  IngestionConfig?: Record<string, unknown> | null;
  // Write-only: stored in Secrets Manager server-side, never returned.
  ApiKey?: string | null;
}

const mapTrpcResponseToGraphQL = (data: {
  Id: string;
}): UpdateIngestionConfigMutation => ({
  updateChildIngestionConfig: {
    __typename: 'IdOutput',
    Id: data.Id,
  },
});

export const useUpdateIngestionConfigTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.ingestionConfig.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.ingestionConfig.getAll.queryKey(),
        });
      },
    }),
  });

  return {
    updateIngestionConfig: async (
      input: UpdateIngestionConfigInput
    ): Promise<UpdateIngestionConfigMutation> => {
      const result = await mutation.mutateAsync({
        Id: input.Id,
        OriginalTimestamp: input.OriginalTimestamp,
        // Loose parsed-JSON object → shared JSONB; identical at runtime.
        IngestionConfig: (input.IngestionConfig ?? null) as JSONB | null,
        ApiKey: input.ApiKey ?? null,
      });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
